"use client"

import { useState } from "react"
import { Form, Input, Button, Typography, message, Alert, Modal, Spin } from "antd"
import { 
  EyeInvisibleOutlined, 
  EyeTwoTone, 
  CreditCardOutlined,
  KeyOutlined,
  LockOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons"

const { Title, Text } = Typography

export default function StripeConnector({ visible, onClose, onSuccess }) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const inputStyle = {
    height: "40px",
    fontSize: "14px",
    padding: "8px 12px",
    width: "100%",
    backgroundColor: "#ffffff",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    transition: "all 0.2s ease",
  }

  const handleSubmit = async (values) => {
    setLoading(true)
    setError(null)
    
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500))
      
      // Validate the connection (you would call your backend here)
      if (!values.accountId || !values.secretKey) {
        throw new Error("Please provide both Account ID and Secret Key")
      }

      // Simulate connection test
      if (values.secretKey.length < 20) {
        throw new Error("Invalid secret key format")
      }

      console.log("Stripe connection:", { accountId: values.accountId })
      
      message.success({
        content: "Stripe connected successfully! Your credentials are valid.",
        icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
        duration: 4,
      })
      
      form.resetFields()
      onSuccess && onSuccess(values)
      onClose()
      
    } catch (error) {
      const errorMessage = error.message || "Connection test failed. Please check your credentials."
      setError(errorMessage)
      message.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setError(null)
    onClose()
  }

  const validateAccountId = (_, value) => {
    if (!value) return Promise.resolve()
    if (value.length < 5) {
      return Promise.reject(new Error("Account ID must be at least 5 characters"))
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
      return Promise.reject(new Error("Account ID can only contain letters, numbers, underscores, and hyphens"))
    }
    return Promise.resolve()
  }

  const validateSecretKey = (_, value) => {
    if (!value) return Promise.resolve()
    if (!value.startsWith('sk_')) {
      return Promise.reject(new Error("Secret key must start with 'sk_'"))
    }
    if (value.length < 20) {
      return Promise.reject(new Error("Secret key must be at least 20 characters"))
    }
    return Promise.resolve()
  }

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              backgroundColor: "#6772e5",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CreditCardOutlined style={{ fontSize: "16px", color: "white" }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
              Connect Stripe
            </Title>
            <Text style={{ color: "#6b7280", fontSize: "13px" }}>
              Enter your API credentials
            </Text>
          </div>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={480}
      centered
      destroyOnClose
      maskClosable={false}
      style={{ top: 20 }}
      bodyStyle={{ padding: "24px" }}
    >
      <Alert
        message="Connection Test Only"
        description="No credentials are saved. We only verify your API keys are valid."
        type="info"
        showIcon
        style={{
          borderRadius: "6px",
          fontSize: "13px",
          marginBottom: "20px",
        }}
      />

      {error && (
        <Alert
          message="Connection Error"
          description={error}
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{
            borderRadius: "6px",
            fontSize: "13px",
            marginBottom: "20px",
          }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        style={{ display: "flex", flexDirection: "column", gap: "16px" }}
      >
        <Form.Item
          label={
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <KeyOutlined style={{ color: "#6772e5", fontSize: "14px" }} />
              <Text style={{ color: "#374151", fontSize: "14px", fontWeight: "500" }}>
                Account ID
              </Text>
              <Text style={{ color: "#ef4444", fontSize: "12px" }}>*</Text>
            </div>
          }
          name="accountId"
          rules={[
            { required: true, message: "Please enter your account ID" },
            { validator: validateAccountId }
          ]}
          style={{ marginBottom: 0 }}
        >
          <Input 
            placeholder="acct_..." 
            style={inputStyle}
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          label={
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <LockOutlined style={{ color: "#ef4444", fontSize: "14px" }} />
              <Text style={{ color: "#374151", fontSize: "14px", fontWeight: "500" }}>
                Secret Key
              </Text>
              <Text style={{ color: "#ef4444", fontSize: "12px" }}>*</Text>
            </div>
          }
          name="secretKey"
          rules={[
            { required: true, message: "Please enter your secret key" },
            { validator: validateSecretKey }
          ]}
          style={{ marginBottom: 0 }}
        >
          <Input.Password
            placeholder="sk_test_..."
            autoComplete="new-password"
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            style={inputStyle}
            disabled={loading}
          />
        </Form.Item>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "20px" }}>
          <Button
            onClick={handleCancel}
            size="middle"
            disabled={loading}
            style={{
              borderRadius: "6px",
              fontSize: "14px",
              padding: "0 16px",
              height: "36px",
              borderColor: "#d1d5db",
              color: "#6b7280",
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            size="middle"
            style={{
              backgroundColor: "#6772e5",
              borderColor: "#6772e5",
              borderRadius: "6px",
              fontSize: "14px",
              padding: "0 20px",
              height: "36px",
              fontWeight: "500",
            }}
          >
            {loading ? "Connecting..." : "Connect Stripe"}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}
